import { useEffect, useRef, useState } from 'react';
import useFeedData from './useFeedData';

// Defaults for a "load more" list: a longer first page so there's something
// worth scrolling before anyone has to press anything, then smaller pages.
export const DEFAULT_INITIAL_COUNT = 40;
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Paging on top of useFeedData for feeds that grow in place behind a Load more
 * button (the country Recents lists).
 *
 * Paging is just a larger `limit`. useFeedData refetches when it changes, keeps
 * the items already on screen while the request is in flight, and its cache key
 * includes the limit — so a press costs one request and never blanks the list.
 *
 * End of feed is detected by result, not by arithmetic. Comparing the returned
 * count against the requested limit assumes the API always fills the request,
 * and any server-side page cap below `initialCount` hides the button on the
 * first load. Instead the button shows whenever there are items and retires
 * only once a press comes back with nothing new — one wasted request at the end
 * of the feed, in exchange for a button that's always reachable.
 *
 * @param {string} apiEndpoint - endpoint to page through; falsy is a no-op
 * @param {Object} [options]
 * @param {number} [options.initialCount] - items requested on first load
 * @param {number} [options.pageSize] - items added per press
 * @returns {Object} items, loading, error, isFirstLoad, hasMore, loadMore
 */
const usePagedFeed = (apiEndpoint, options = {}) => {
  const initialCount = options.initialCount || DEFAULT_INITIAL_COUNT;
  const pageSize = options.pageSize || DEFAULT_PAGE_SIZE;

  const [limit, setLimit] = useState(initialCount);
  const [reachedEnd, setReachedEnd] = useState(false);
  const lastCountRef = useRef(0);
  const wasLoadingRef = useRef(false);

  const { data, loading, error } = useFeedData(apiEndpoint, limit);
  const items = Array.isArray(data) ? data : [];

  // Evaluate only on the loading -> settled transition, never on the render
  // where `limit` changed. Raising the limit re-runs this effect before React
  // has applied useFeedData's setLoading(true), so `loading` is still the stale
  // false from the previous render — checking it directly declares the feed
  // exhausted on the first press and hides the button. wasLoadingRef marks that
  // a request actually went out, so the count comparison only happens against a
  // response that came back.
  useEffect(() => {
    if (loading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;

    if (limit > initialCount && items.length <= lastCountRef.current) {
      setReachedEnd(true);
    }
    lastCountRef.current = Math.max(lastCountRef.current, items.length);
  }, [loading, items.length, limit, initialCount]);

  // A different feed is a different list: start over rather than carrying the
  // previous feed's page position and exhausted flag into it.
  useEffect(() => {
    setLimit(initialCount);
    setReachedEnd(false);
    lastCountRef.current = 0;
    wasLoadingRef.current = false;
  }, [apiEndpoint, initialCount]);

  const loadMore = () => setLimit((current) => current + pageSize);

  return {
    items,
    loading,
    error,
    isFirstLoad: loading && items.length === 0,
    hasMore: !error && !reachedEnd && items.length > 0,
    loadMore,
  };
};

export default usePagedFeed;